import axios from 'axios';
import {
  AuthenticationLoginUseCaseInterface,
  AuthenticationLogoutUseCaseInterface,
  AuthenticationMeUseCaseInterface,
} from 'domain/usecases';
import Cookies from 'js-cookie';
import { WithChildren } from 'presentation/config/helpers/react18MigrationHelpers';
import {
  FC,
  Dispatch,
  useState,
  useContext,
  createContext,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import { useService } from './useService';

type AuthContextProps = {
  logout: () => void;
  auth: AuthenticationLoginUseCaseInterface.Result | undefined;
  currentUser: AuthenticationLoginUseCaseInterface.Result | undefined;
  saveAuth: (
    auth: AuthenticationLoginUseCaseInterface.Result | undefined,
  ) => void;
  setCurrentUser: Dispatch<
    SetStateAction<AuthenticationLoginUseCaseInterface.Result | undefined>
  >;
  changeDataProfileCookies: (params: { photo?: string }) => void;
  requestMe: () => Promise<AuthenticationMeUseCaseInterface.Result | undefined>;
  loadingGlobal?: boolean;
};

const initAuthContextPropsState: AuthContextProps = {
  auth: undefined,
  logout: () => {},
  saveAuth: () => {},
  loadingGlobal: true,
  currentUser: undefined,
  setCurrentUser: () => {},
  requestMe: async () => undefined,
  changeDataProfileCookies: () => {},
};

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState);

const useAuth = () => {
  return useContext(AuthContext);
};

const AuthProvider: FC<WithChildren> = ({ children }) => {
  const getLoggedUser = () => {
    const user = Cookies.get('authUser');
    if (!user) {
      return undefined;
    } else {
      return JSON.parse(user);
    }
  };

  const changeDataProfileCookies = (params) => {
    setCurrentUser({
      ...currentUser,
      photo: params.photo,
    });
    setAuth({
      ...auth,
      photo: params.photo,
    });
    Cookies.set(
      'authUser',
      JSON.stringify({
        ...currentUser,
        id: currentUser.id,
        photo: params.photo,
      }),
    );
  };

  const { authenticationService } = useService();

  // Flag para evitar loop no interceptor
  const isLoggingOut = useRef(false);

  const requestLogout = useCallback(async () => {
    return authenticationService
      .authenticationLogout(undefined)
      .then((res) => {})
      .catch((err) => {});
  }, []);

  const [loadingGlobal, setLoadingGlobal] = useState(true);
  const [initialized, setInitialized] = useState(false);

  const [auth, setAuth] = useState<
    AuthenticationLoginUseCaseInterface.Result | undefined
  >(getLoggedUser());
  const [currentUser, setCurrentUser] = useState<
    AuthenticationLoginUseCaseInterface.Result | undefined
  >(getLoggedUser());

  const saveAuth = (
    auth: AuthenticationLoginUseCaseInterface.Result | undefined,
  ) => {
    setAuth(auth);
  };

  const logout = useCallback(() => {
    if (isLoggingOut.current) {
      return;
    }

    isLoggingOut.current = true;
    
    saveAuth(undefined);
    setCurrentUser(undefined);
    Cookies.remove('authUser');
    
    requestLogout().finally(() => {
      isLoggingOut.current = false;
    });
  }, [requestLogout]);

  // Configurar interceptor apenas uma vez
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      function (response) {
        return response;
      },
      function (error) {
        if (error.response) {
          if (error.response.status === 403 && (auth || currentUser)) {
            console.log('403 error detected:', { 
              hasAuth: !!auth, 
              hasCurrentUser: !!currentUser,
              isLoggingOut: isLoggingOut.current 
            });
            
            // Só executa logout se não estiver já fazendo logout
            if (!isLoggingOut.current) {
              logout();
            }
          }
        }
        return Promise.reject(error);
      },
    );

    // Cleanup do interceptor quando componente for desmontado
    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, [auth, currentUser, logout]);

  const isRestoredSession = !!getLoggedUser();

  const requestMe = useCallback(async () => {
    setLoadingGlobal(true);
    try {
      const response = await authenticationService.authenticationMe();
      return response;
    } catch (err) {
      return undefined;
    } finally {
      setLoadingGlobal(false);
    }
  }, []);

  useEffect(() => {
    if (!initialized && auth && isRestoredSession) {
      setLoadingGlobal(true);
      requestMe().finally(() => {
        setLoadingGlobal(false);
        setInitialized(true);
      });
    } else {
      setLoadingGlobal(false);
      setInitialized(true);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        auth,
        logout,
        saveAuth,
        requestMe,
        currentUser,
        loadingGlobal,
        setCurrentUser,
        changeDataProfileCookies,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthProvider, useAuth };
